import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import {
  CheckCircle2, ChefHat, Clock, MapPin, Package, Phone, Truck, XCircle,
} from "lucide-react";
import PageHeader from "../shared/PageHeader";
import { getOrderById } from "../../services/order.api";
import RiderCard from "./RiderCard";
import "./styles/orderTracking.css";

/**
 * The order lifecycle the API exposes, in order. Each step lights up once the
 * order has reached it; the timestamps come from the order's own audit fields
 * (only some stages record one, so a step can be reached without a time).
 */
const STEPS = [
  { key: "PENDING", label: "Order placed", icon: Package, at: "createdAt" },
  { key: "CONFIRMED", label: "Confirmed by kitchen", icon: CheckCircle2, at: "confirmedAt" },
  { key: "PREPARING", label: "Being prepared", icon: ChefHat, at: null },
  { key: "READY", label: "Ready", icon: Clock, at: "readyAt" },
  { key: "OUT_FOR_DELIVERY", label: "Out for delivery", icon: Truck, at: null },
  { key: "DELIVERED", label: "Delivered", icon: CheckCircle2, at: "deliveredAt" },
];

const fmtTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

export default function OrderTracking() {
  const { id } = useParams();

  const { data, isPending, error } = useQuery({
    queryKey: ["order-tracking", id],
    queryFn: () => getOrderById(id),
    enabled: !!id,
    // The kitchen (or, for OwnFood delivery, the rider) moves the order along
    // on their own side.
    refetchInterval: 30000,
  });

  const order = data?.data;
  const cancelled = order?.rawStatus === "cancelled";
  const currentIndex = STEPS.findIndex(
    (s) => s.key === String(order?.rawStatus || "").toUpperCase(),
  );

  return (
    <div className="container page-navbar-space">
      <PageHeader>Track Your Order</PageHeader>

      <div className="ot-page mt-5 pb-5">
        {isPending && <Skeleton height={280} borderRadius={14} />}

        {(error || (!isPending && !order)) && (
          <div
            style={{
              padding: 16, borderRadius: 12, background: "#fee2e2",
              color: "#991b1b", fontSize: 13, fontFamily: "'Inter', sans-serif",
            }}
          >
            We couldn't load this order.
          </div>
        )}

        {order && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Header */}
            <div
              style={{
                background: "#fff", border: "1.5px solid #f1f5f9",
                borderRadius: 14, padding: 20, fontFamily: "'Inter', sans-serif",
              }}
            >
              <div
                style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "flex-start", gap: 12, flexWrap: "wrap",
                }}
              >
                <div>
                  <p style={{ margin: "0 0 4px", fontSize: 12, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Order #{order.orderNumber}
                  </p>
                  <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0f172a" }}>
                    {order.statusLabel}
                  </h2>
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
                    {order.deliveryMethod === "SELF"
                      ? "Delivered by the kitchen"
                      : "Delivered by an OwnFood rider"}
                  </p>
                </div>

                {order.chefPhone && (
                  <a
                    href={`tel:${order.chefPhone}`}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "9px 16px", borderRadius: 10, background: "#0d6efd",
                      color: "#fff", fontSize: 13, fontWeight: 700, textDecoration: "none",
                    }}
                  >
                    <Phone size={14} /> Call kitchen
                  </a>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div
              style={{
                background: "#fff", border: "1.5px solid #f1f5f9",
                borderRadius: 14, padding: 20, fontFamily: "'Inter', sans-serif",
              }}
            >
              {cancelled ? (
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <XCircle size={22} color="#ef4444" />
                  <div>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#991b1b" }}>
                      This order was cancelled
                    </p>
                    {order.cancelledReason && (
                      <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
                        {order.cancelledReason}
                      </p>
                    )}
                    {order.cancelledAt && (
                      <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>
                        {fmtTime(order.cancelledAt)}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {STEPS.map((step, i) => {
                    const reached = currentIndex >= i;
                    const Icon = step.icon;
                    const at = step.at ? order[step.at] : null;
                    return (
                      <div key={step.key} style={{ display: "flex", gap: 12 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <div
                            style={{
                              width: 30, height: 30, borderRadius: "50%",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              background: reached ? "#0d6efd" : "#f1f5f9",
                              color: reached ? "#fff" : "#9ca3af",
                              flexShrink: 0,
                            }}
                          >
                            <Icon size={15} />
                          </div>
                          {i < STEPS.length - 1 && (
                            <div
                              style={{
                                width: 2, flex: 1, minHeight: 22,
                                background: currentIndex > i ? "#0d6efd" : "#e5e7eb",
                              }}
                            />
                          )}
                        </div>
                        <div style={{ paddingBottom: 14 }}>
                          <p
                            style={{
                              margin: 0, fontSize: 14,
                              fontWeight: reached ? 700 : 500,
                              color: reached ? "#111827" : "#9ca3af",
                            }}
                          >
                            {step.label}
                          </p>
                          {at && (
                            <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>
                              {fmtTime(at)}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {!cancelled && <RiderCard order={order} />}

            {/* Delivery address */}
            {order.deliveryAddress && (
              <div
                style={{
                  background: "#fff", border: "1.5px solid #f1f5f9",
                  borderRadius: 14, padding: 20, fontFamily: "'Inter', sans-serif",
                }}
              >
                <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Delivering to
                </p>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111827" }}>
                  <MapPin size={13} style={{ marginRight: 5 }} />
                  {order.deliveryAddress.label}
                </p>
                <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
                  {order.deliveryAddress.address}
                </p>
              </div>
            )}

            <Link
              to={`/order-details/${order._id}`}
              style={{
                display: "block", textAlign: "center", padding: "11px",
                borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff",
                fontSize: 13, fontWeight: 600, color: "#374151",
                textDecoration: "none", fontFamily: "'Inter', sans-serif",
              }}
            >
              View order details
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
